import { DependencyList, useCallback, useEffect, useRef, useState } from 'react'

export type AsyncOptionsStatus = 'loading' | 'ready' | 'error'

/**
 * Loads select options once per dependency change.
 * Ignores a slower response after the inputs change, and exposes retry.
 */
export function useAsyncOptions<T>(
  fetcher: () => Promise<T>,
  deps: DependencyList,
  enabled = true
) {
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const [data, setData] = useState<T | null>(null)
  const [status, setStatus] = useState<AsyncOptionsStatus>(
    enabled ? 'loading' : 'ready'
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!enabled) {
      setData(null)
      setStatus('ready')
      return
    }
    let cancelled = false
    setData(null)
    setStatus('loading')
    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return
        setData(result)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
    // fetcher is read from a ref so a new closure does not restart the request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, attempt, ...deps])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { data, status, retry }
}
